import { useState } from 'react';

import { ChevronDown, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { FLOOD_DEPTH_COLORS, FLOOD_DEPTH_LABELS } from '@/config/colors';

import { getInfraVisual } from '../../utils/markerVisual';

export function MapIconLegend() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  // Helper to render SVG mask with background color
  const renderIcon = (url: string, colorArray: [number, number, number, number], size = 16) => {
    const rgba = `rgba(${colorArray[0]}, ${colorArray[1]}, ${colorArray[2]}, ${colorArray[3] / 255})`;
    return (
      <div
        style={{
          width: size,
          height: size,
          backgroundColor: rgba,
          WebkitMaskImage: `url(${url})`,
          WebkitMaskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskImage: `url(${url})`,
          maskSize: 'contain',
          maskRepeat: 'no-repeat',
          maskPosition: 'center',
        }}
      />
    );
  };

  const infraTypes = ['pump', 'culvert', 'canal', 'dyke', 'lowlying', 'landslide'] as const;

  return (
    <div
      style={{
        width: 240,
        background: 'var(--map-chrome-panel)',
        backdropFilter: 'blur(16px)',
        borderRadius: 16,
        border: '1px solid var(--glass-border)',
        padding: '12px',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          userSelect: 'none',
          padding: '2px 4px',
        }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MapPin size={13} color="var(--neon-cyan)" strokeWidth={2.5} />
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}
          >
            {t('mapIconLegend.title')}
          </span>
        </div>
        <ChevronDown
          size={14}
          color="var(--text-secondary)"
          style={{
            transition: 'transform 0.3s ease',
            transform: collapsed ? 'rotate(-90deg)' : 'none',
          }}
        />
      </div>

      {!collapsed && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            maxHeight: '20vh', // Slightly reduced to ensure it floats above bottom
            overflowY: 'auto',
            paddingRight: '4px',
          }}
          className="custom-scrollbar"
        >
          {/* Flood grid depth scale */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 6,
                padding: '0 4px',
              }}
            >
              <div
                style={{ width: 2, height: 10, background: 'var(--neon-cyan)', borderRadius: 1 }}
              />
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {t('mapIconLegend.floodGridDepth')}
              </span>
            </div>
            <p
              style={{
                fontSize: 9,
                color: 'var(--text-muted)',
                margin: '0 0 8px 12px',
                lineHeight: 1.35,
              }}
            >
              {t('mapIconLegend.floodGridDepthCaption')}
            </p>
            <div style={{ paddingLeft: 12, paddingRight: 4 }}>
              <div
                style={{
                  height: 10,
                  borderRadius: 4,
                  marginBottom: 8,
                  border: '1px solid var(--glass-border)',
                  background: `linear-gradient(to right, ${FLOOD_DEPTH_COLORS.map(
                    (c, i, arr) =>
                      `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${c[3] / 255}) ${(i / Math.max(arr.length - 1, 1)) * 100}%`,
                  ).join(', ')})`,
                }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 4,
                  flexWrap: 'wrap',
                }}
              >
                {FLOOD_DEPTH_LABELS.map((label) => (
                  <span
                    key={label}
                    style={{ fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Infrastructure */}
          <div style={{ borderTop: '1px solid var(--glass-border)', paddingTop: 10 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 6,
                padding: '0 4px',
              }}
            >
              <div style={{ width: 2, height: 10, background: '#eab308', borderRadius: 1 }} />
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {t('mapIconLegend.infrastructure')}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingLeft: 12 }}>
              {infraTypes.map((type) => {
                const visual = getInfraVisual(type);
                return (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {renderIcon(visual.url, visual.color)}
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {t(`mapIconLegend.infraTypes.${type}`)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
